'use client';

import { useEffect, useState } from 'react';

import {
  chartChrome,
  chartColorFor,
  chartSeries,
  chartStatus,
  chartTooltipStyle,
  type ChartMode,
} from '../theme/charts';

/**
 * AURORA DS — paleta wykresów dopasowana do bieżącego trybu.
 *
 * DLACZEGO HOOK, A NIE TOKENY CSS. Biblioteki wykresów rysują po `<canvas>`
 * albo przekazują kolor dalej jako zwykły string (`cursor`, `<linearGradient>`,
 * własne tooltipy Rechartsa), gdzie `var(--ids-*)` nie ma czego rozwiązać — nie
 * ma tam kaskady. Dlatego wykresy potrzebują wartości DOSŁOWNYCH, a wybór
 * między paletą ciemną i jasną musi się odbyć w JS.
 *
 * Źródłem prawdy o trybie jest `data-mode` na `<html>` — ten sam atrybut, który
 * czyta cała Aurora i który ustawia `ThemeProvider`. Obserwujemy go, więc
 * przełączenie motywu przerysowuje wykres bez przeładowania strony.
 *
 * Stan startowy czytamy dopiero w efekcie, nie w inicjalizatorze `useState` —
 * przy SSR (moduły są na Next) `document` nie istnieje, a serwer i pierwszy
 * render klienta muszą dać ten sam HTML.
 */
export function useChartTheme() {
  const [mode, setMode] = useState<ChartMode>('dark');

  useEffect(() => {
    const root = document.documentElement;
    const read = (): ChartMode => (root.getAttribute('data-mode') === 'light' ? 'light' : 'dark');

    setMode(read());

    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-mode'] });
    return () => observer.disconnect();
  }, []);

  const chrome = chartChrome(mode);

  return {
    mode,
    /** Siatka, osie, ticki, tło i obramowanie tooltipa, tekst. */
    chrome,
    /** Skrótowe aliasy — czytelniejsze w propsach Rechartsa. */
    grid: chrome.grid,
    axis: chrome.axis,
    tick: chrome.tick,
    text: chrome.text,
    /** Gotowy `contentStyle` dla `<Tooltip>`. */
    tooltipStyle: chartTooltipStyle(mode),
    /** Kolejność serii i kolor n-tej serii. */
    series: chartSeries(mode),
    color: (index: number) => chartColorFor(mode, index),
    /** Semantyka stanu — ok / warn / bad / info / idle. */
    status: chartStatus(mode),
  };
}
